import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoffee,
  faUser,
  faStar,
  faCog,
  faHome,
  faSearch,
  faBell,
  faEnvelope,
  faHeart,
  faTh,
  faList,
  faChartBar,
  faMapMarkerAlt,
  faTags,
  faGlobe,
  faBinoculars,
  faLayerGroup,
  faFilter,
  faSave,
  faPen,
  faMoneyBill,
  faChartBar as faChartLineDuotone,
  faBuilding as faBuildingDuotone,
  faCalendar,
  faCamera,
  faCloud,
  faComment,
  faDownload,
  faEdit,
  faEye,
  faBuilding,
  faChartPie,
  faBook,
  faTree,
  faCar,
  faAppleWhole,
  faRocket,
  faPoo,
  faBookmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { ColorPicker } from "./ColorPicker";

interface IconPickerItem {
  name: string;
  definition: IconDefinition;
}

// Define a list of available icons
// Ensure all IconDefinitions are imported above
export const PREDEFINED_ICONS: IconPickerItem[] = [
  { name: "rocket", definition: faRocket },
  { name: "poo", definition: faPoo },
  { name: "star", definition: faStar },
  { name: "cog", definition: faCog },
  { name: "home", definition: faHome },
  { name: "search", definition: faSearch },
  { name: "bell", definition: faBell },
  { name: "envelope", definition: faEnvelope },
  { name: "heart", definition: faHeart },
  { name: "th", definition: faTh },
  { name: "list", definition: faList },
  { name: "chart-bar", definition: faChartBar },
  { name: "map-marker-alt", definition: faMapMarkerAlt },
  { name: "tags", definition: faTags },
  { name: "globe", definition: faGlobe },
  { name: "binoculars", definition: faBinoculars },
  { name: "layer-group", definition: faLayerGroup },
  { name: "filter", definition: faFilter },
  { name: "money-bill", definition: faMoneyBill },
  { name: "calendar", definition: faCalendar },
  { name: "camera", definition: faCamera },
  { name: "cloud", definition: faCloud },
  { name: "comment", definition: faComment },
  { name: "eye", definition: faEye },
  { name: "building", definition: faBuilding },
  { name: "chart-pie", definition: faChartPie },
  { name: "book", definition: faBook },
  { name: "tree", definition: faTree },
  { name: "car", definition: faCar },
  { name: "apple-whole", definition: faAppleWhole },
  { name: "bookmark", definition: faBookmark },
];

interface IconPickerProps {
  onSelectIcon: (iconName: string) => void;
  currentIcon?: string;
  currentColor?: string;
  onSelectColor?: (color: string) => void;
  onColorPopoverOpenChange?: (open: boolean) => void;
}

export function IconPicker({
  onSelectIcon,
  currentIcon,
  currentColor,
  onSelectColor,
  onColorPopoverOpenChange,
}: IconPickerProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [tab, setTab] = useState<"icons" | "emojis">("icons");

  const filteredIcons = useMemo(() => {
    if (!searchTerm) {
      return PREDEFINED_ICONS;
    }
    return PREDEFINED_ICONS.filter((icon) =>
      icon.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  // 30 emojis matching the icon themes
  const EMOJIS = [
    "🚀", // coffee
    "💩", // user
    "⭐️", // star
    "⚙️", // cog
    "🏠", // home
    "🔍", // search
    "🔔", // bell
    "✉️", // envelope
    "❤️", // heart
    "🟦", // th/grid
    "📋", // list
    "📊", // chart-bar
    "📍", // map-marker
    "🏷️", // tags
    "🌍", // globe
    "🔭", // binoculars
    "🗂️", // layer-group
    "🧹", // filter
    "💵", // money-bill
    "📅", // calendar
    "📷", // camera
    "☁️", // cloud
    "💬", // comment
    "👁️", // eye
    "🏢", // building
    "🥧", // chart-pie
    "📚", // book
    "🌳", // tree
    "🚗", // car
    "🍏", // apple
  ];

  return (
    <div
      className=" pb-2 "
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 mb-6">
        <input
          type="text"
          placeholder="Find your style..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 text-sm bg-slate-100 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder-slate-400"
          onClick={(e) => e.stopPropagation()}
        />

        <ColorPicker
          currentColor={currentColor}
          onSelectColor={onSelectColor ?? (() => {})}
          onPopoverOpenChange={onColorPopoverOpenChange}
        />
      </div>
      {/* Tab system below search/color picker */}
      <div className="flex mb-4 gap-2 text-xs font-mono uppercase">
        <button
          className={`px-1 py-1 rounded-t-md uppercase tracking-widest border-b-2 transition-colors ${
            tab === "icons"
              ? "border-slate-800 text-slate-800 dark:text-slate-300"
              : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            setTab("icons");
          }}
        >
          Icons
        </button>
        <button
          className={`px-1 py-1 rounded-t-md uppercase tracking-widest border-b-2 transition-colors ${
            tab === "emojis"
              ? "border-slate-800 text-slate-800 dark:text-slate-300"
              : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            setTab("emojis");
          }}
        >
          Emoji
        </button>
      </div>
      {tab === "icons" && (
        <>
          <div
            className="grid grid-cols-6 gap-1"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {filteredIcons.map((icon) => (
              <button
                key={icon.name}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectIcon(icon.name);
                }}
                className={`flex items-center justify-center p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                  currentIcon === icon.name
                    ? "bg-slate-100 dark:bg-slate-700 "
                    : "bg-transparent"
                }`}
                title={icon.name}
              >
                <FontAwesomeIcon
                  icon={icon.definition}
                  style={{ color: currentColor || "#9e9e9e" }}
                  fixedWidth
                />
              </button>
            ))}
            {filteredIcons.length === 0 && (
              <p className="col-span-6 text-center text-sm text-slate-500 dark:text-slate-400 py-4">
                No icons found.
              </p>
            )}
          </div>
        </>
      )}
      {tab === "emojis" && (
        <div
          className="grid grid-cols-6 gap-0"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              onClick={(e) => {
                e.stopPropagation();
                onSelectIcon(emoji);
              }}
              className="flex items-center justify-center p-2 rounded-md text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
